import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { todayIso as todayIsoUtil, toIsoDate } from '../utils/dateUtils';

const POSTING_TYPE_LABELS = {
  ROOM_TARIFF: 'Room Tariff', LAUNDRY: 'Laundry', ROOM_SERVICE: 'Room Service',
  MINIBAR_ROOM_CHARGE: 'Minibar', KOT_ROOM_CHARGE: 'KOT Room Charge', BAR_ROOM_CHARGE: 'Bar Room Charge',
  LATE_CHECKOUT: 'Late Checkout', MANUAL: 'Manual Charge', ALLOWANCE: 'Allowance',
  PAYMENT: 'Payment', CHECKOUT_PAYMENT: 'Checkout Payment', ADVANCE: 'Advance',
  REFUND: 'Refund', VOID: 'Void'
};

const REVENUE_CODE_MAP = {
  ROOM_TARIFF: 'RC_ROOM', LAUNDRY: 'RC_LAUNDRY', ROOM_SERVICE: 'RC_ROOM_SERVICE',
  MINIBAR_ROOM_CHARGE: 'RC_MINIBAR', KOT_ROOM_CHARGE: 'RC_KOT', BAR_ROOM_CHARGE: 'RC_BAR',
  LATE_CHECKOUT: 'RC_LATE_CO', ALLOWANCE: 'RC_ALLOWANCE', MANUAL: 'RC_MISC'
};

const CHARGE_TYPES = new Set(['ROOM_TARIFF', 'LAUNDRY', 'ROOM_SERVICE', 'MINIBAR_ROOM_CHARGE', 'KOT_ROOM_CHARGE', 'BAR_ROOM_CHARGE', 'LATE_CHECKOUT', 'MANUAL']);
const PAYMENT_TYPES = new Set(['PAYMENT', 'CHECKOUT_PAYMENT', 'ADVANCE']);
const CREDIT_NOTE_TYPES = new Set(['REFUND', 'ALLOWANCE', 'VOID']);

function escapeXml(str) {
  return String(str || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;');
}

function escapeCsv(val) {
  const str = String(val ?? '');
  return (str.includes(',') || str.includes('"') || str.includes('\n')) ? `"${str.replace(/"/g, '""')}"` : str;
}

function fmtMoney(v) { return Number(v || 0).toFixed(2); }

function toTallyDate(dateStr) { return String(dateStr || '').replace(/-/g, ''); }

function todayStr() { return todayIsoUtil(); }
function monthStartStr() { const t = todayIsoUtil(); return t.slice(0, 7) + '-01'; }

export default function AccountingTallyExport({ token, selectedProperty }) {
  const [fromDate, setFromDate] = useState(monthStartStr);
  const [toDate, setToDate] = useState(todayStr);
  const [module, setModule] = useState('ALL');
  const [mode, setMode] = useState('detail');
  const [format, setFormat] = useState('csv');
  const [data, setData] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const headers = useCallback(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  }), [token, selectedProperty]);

  // Load config on mount
  useEffect(() => {
    if (!selectedProperty?.id) return;
    fetch('/api/v1/accounting-export/config', { headers: headers() })
      .then(r => r.json())
      .then(c => setConfig(c))
      .catch(() => {});
  }, [selectedProperty, headers]);

  const handlePreview = async () => {
    setLoading(true);
    setError('');
    setData(null);
    try {
      const params = new URLSearchParams({ from: fromDate, to: toDate, module, mode });
      const resp = await fetch(`/api/v1/accounting-export/data?${params}`, { headers: headers() });
      if (!resp.ok) {
        const e = await resp.json();
        throw new Error(e.error || 'Failed to fetch data');
      }
      const result = await resp.json();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Totals ──
  const totals = useMemo(() => {
    if (!data) return { charges: 0, payments: 0, tax: 0, net: 0 };
    let charges = 0, payments = 0, tax = 0;
    if (mode === 'summary') {
      (data.pms_data || []).forEach(r => {
        charges += Number(r.total_debit || 0);
        payments += Number(r.total_credit || 0);
        tax += Number(r.total_tax || 0);
      });
      (data.outlet_data || []).forEach(r => {
        charges += Number(r.total_subtotal || 0);
        tax += Number(r.total_cgst || 0) + Number(r.total_sgst || 0) + Number(r.total_igst || 0);
        payments += Number(r.total_amount || 0);
      });
    } else {
      (data.pms_data || []).forEach(r => {
        charges += Number(r.debit_amount || 0);
        payments += Number(r.credit_amount || 0);
        tax += Number(r.tax_amount || 0);
      });
      (data.outlet_data || []).forEach(r => {
        charges += Number(r.subtotal || 0);
        tax += Number(r.cgst_amount || 0) + Number(r.sgst_amount || 0) + Number(r.igst_amount || 0);
        payments += Number(r.total_amount || 0);
      });
    }
    return { charges, payments, tax, net: charges - payments + tax };
  }, [data, mode]);

  // ── CSV Download ──
  const downloadCSV = () => {
    if (!data) return;
    let csvContent = '';
    if (mode === 'summary') {
      csvContent = 'Date,Source,Category,Total Charges,Total Tax,CGST,SGST,IGST,Total Payments,Txn Count\r\n';
      (data.pms_data || []).forEach(r => {
        csvContent += [r.posting_date, 'PMS', r.revenue_desc || r.posting_type, fmtMoney(r.total_debit), fmtMoney(r.total_tax), fmtMoney(r.total_cgst), fmtMoney(r.total_sgst), fmtMoney(r.total_igst), fmtMoney(r.total_credit), r.txn_count].map(escapeCsv).join(',') + '\r\n';
      });
      (data.outlet_data || []).forEach(r => {
        csvContent += [r.posting_date, r.outlet_type, `${r.outlet_type} - ${r.payment_type}`, fmtMoney(r.total_subtotal), fmtMoney(Number(r.total_cgst || 0) + Number(r.total_sgst || 0) + Number(r.total_igst || 0)), fmtMoney(r.total_cgst), fmtMoney(r.total_sgst), fmtMoney(r.total_igst), fmtMoney(r.total_amount), r.txn_count].map(escapeCsv).join(',') + '\r\n';
      });
    } else {
      csvContent = 'Date,Source,Invoice No,Type,Description,Debit,Credit,CGST,SGST,IGST,Guest Name,Room No,Reservation No,Settlement Mode\r\n';
      (data.pms_data || []).forEach(r => {
        csvContent += [r.posting_date, 'PMS', r.invoice_no || '', r.posting_type, r.revenue_desc || r.remarks || '', fmtMoney(r.debit_amount), fmtMoney(r.credit_amount), fmtMoney(r.cgst_amount), fmtMoney(r.sgst_amount), fmtMoney(r.igst_amount), r.guest_name || '', r.room_number || '', r.reservation_no || '', r.settlement_mode || ''].map(escapeCsv).join(',') + '\r\n';
      });
      (data.outlet_data || []).forEach(r => {
        csvContent += [r.posting_date, r.outlet_type, '', 'ORDER_SETTLEMENT', `${r.outlet_name || r.outlet_type} Order #${r.order_no}`, fmtMoney(r.subtotal), fmtMoney(r.total_amount), fmtMoney(r.cgst_amount), fmtMoney(r.sgst_amount), fmtMoney(r.igst_amount), r.guest_name || '', r.room_number || '', '', r.payment_type || ''].map(escapeCsv).join(',') + '\r\n';
      });
    }
    downloadFile(csvContent, `TallyExport_${fromDate}_${toDate}.csv`, 'text/csv;charset=utf-8;');
  };

  // ── Tally XML Download ──
  const downloadXML = () => {
    if (!data) return;
    const m = config?.mappings || {};
    const rev = m.revenue || {};
    const pay = m.payment || {};
    const tax = m.tax || {};
    const debtorLedger = m.debtor || 'Sundry Debtors - Guests';
    const advanceLedger = m.advance || 'Advance from Guests';
    const kotSales = m.kot_sales || 'F&B Sales - KOT';
    const barSales = m.bar_sales || 'Bar Sales';
    const companyName = config?.tally_company_name || selectedProperty?.name || 'Hotel';

    let vouchers = '';
    let vNo = 1;

    // PMS folio entries
    (data.pms_data || []).forEach(r => {
      const dateStr = toTallyDate(r.posting_date);
      const pt = r.posting_type;
      const debit = Number(r.debit_amount || 0);
      const credit = Number(r.credit_amount || 0);
      const cgst = Number(r.cgst_amount || 0);
      const sgst = Number(r.sgst_amount || 0);
      const igst = Number(r.igst_amount || 0);
      const taxTotal = cgst + sgst + igst;
      const narration = [r.revenue_desc || POSTING_TYPE_LABELS[pt] || pt, r.guest_name, r.room_number ? `Room ${r.room_number}` : '', r.invoice_no ? `Inv: ${r.invoice_no}` : ''].filter(Boolean).join(' | ');
      const remoteId = `PMS-${String(vNo).padStart(6, '0')}`;

      let entries = '';
      if (CHARGE_TYPES.has(pt)) {
        const revCode = r.revenue_code || REVENUE_CODE_MAP[pt] || 'RC_MISC';
        const revLedger = rev[revCode] || 'Miscellaneous Income';
        const baseAmount = debit - taxTotal;
        entries += ledgerEntry(debtorLedger, debit, true);
        entries += ledgerEntry(revLedger, -baseAmount, false);
        if (cgst > 0) entries += ledgerEntry(tax.CGST || 'CGST Output', -cgst, false);
        if (sgst > 0) entries += ledgerEntry(tax.SGST || 'SGST Output', -sgst, false);
        if (igst > 0) entries += ledgerEntry(tax.IGST || 'IGST Output', -igst, false);
        vouchers += voucherBlock(remoteId, 'Sales', dateStr, narration, r.invoice_no, entries);
      } else if (PAYMENT_TYPES.has(pt)) {
        const payLedger = pay[r.settlement_mode] || pay.Cash || 'Cash';
        const targetLedger = pt === 'ADVANCE' ? advanceLedger : debtorLedger;
        entries += ledgerEntry(payLedger, credit, true);
        entries += ledgerEntry(targetLedger, -credit, false);
        vouchers += voucherBlock(remoteId, 'Receipt', dateStr, narration, r.invoice_no, entries);
      } else if (CREDIT_NOTE_TYPES.has(pt)) {
        const revCode = r.revenue_code || 'RC_MISC';
        const revLedger = rev[revCode] || 'Miscellaneous Income';
        entries += ledgerEntry(revLedger, credit, true);
        entries += ledgerEntry(debtorLedger, -credit, false);
        vouchers += voucherBlock(remoteId, 'Credit Note', dateStr, narration, r.invoice_no, entries);
      }
      vNo++;
    });

    // KOT/BAR outlet entries
    (data.outlet_data || []).forEach(r => {
      const dateStr = toTallyDate(r.posting_date);
      const subtotal = Number(r.subtotal || 0);
      const total = Number(r.total_amount || 0);
      const cgst = Number(r.cgst_amount || 0);
      const sgst = Number(r.sgst_amount || 0);
      const igst = Number(r.igst_amount || 0);
      const salesLedger = r.outlet_type === 'BAR' ? barSales : kotSales;
      const payLedger = pay[r.payment_type] || pay.Cash || 'Cash';
      const narration = `${r.outlet_name || r.outlet_type} Order #${r.order_no} | ${r.payment_type || 'Cash'}`;
      const remoteId = `OUT-${String(vNo).padStart(6, '0')}`;

      let entries = '';
      entries += ledgerEntry(payLedger, total, true);
      entries += ledgerEntry(salesLedger, -subtotal, false);
      if (cgst > 0) entries += ledgerEntry(tax.CGST || 'CGST Output', -cgst, false);
      if (sgst > 0) entries += ledgerEntry(tax.SGST || 'SGST Output', -sgst, false);
      if (igst > 0) entries += ledgerEntry(tax.IGST || 'IGST Output', -igst, false);
      vouchers += voucherBlock(remoteId, 'Sales', dateStr, narration, null, entries);
      vNo++;
    });

    const xml = `<?xml version="1.0" encoding="UTF-8"?>\n<ENVELOPE>\n  <HEADER><TALLYREQUEST>Import Data</TALLYREQUEST></HEADER>\n  <BODY><IMPORTDATA>\n    <REQUESTDESC>\n      <REPORTNAME>Vouchers</REPORTNAME>\n      <STATICVARIABLES>\n        <SVCURRENTCOMPANY>${escapeXml(companyName)}</SVCURRENTCOMPANY>\n      </STATICVARIABLES>\n    </REQUESTDESC>\n    <REQUESTDATA>${vouchers}\n    </REQUESTDATA>\n  </IMPORTDATA></BODY>\n</ENVELOPE>`;

    downloadFile(xml, `TallyExport_${fromDate}_${toDate}.xml`, 'application/xml;charset=utf-8;');
  };

  function ledgerEntry(ledger, amount, isDebit) {
    return `\n            <ALLLEDGERENTRIES.LIST>\n              <LEDGERNAME>${escapeXml(ledger)}</LEDGERNAME>\n              <ISDEEMEDPOSITIVE>${isDebit ? 'Yes' : 'No'}</ISDEEMEDPOSITIVE>\n              <AMOUNT>${Number(amount).toFixed(2)}</AMOUNT>\n            </ALLLEDGERENTRIES.LIST>`;
  }

  function voucherBlock(remoteId, vchType, dateStr, narration, invoiceNo, entries) {
    return `\n      <TALLYMESSAGE xmlns:UDF="TallyUDF">\n        <VOUCHER REMOTEID="${escapeXml(remoteId)}" VCHTYPE="${escapeXml(vchType)}" ACTION="Create">\n          <DATE>${dateStr}</DATE>\n          <NARRATION>${escapeXml(narration)}</NARRATION>\n          <VOUCHERTYPENAME>${escapeXml(vchType)}</VOUCHERTYPENAME>${invoiceNo ? `\n          <VOUCHERNUMBER>${escapeXml(invoiceNo)}</VOUCHERNUMBER>` : ''}${entries}\n        </VOUCHER>\n      </TALLYMESSAGE>`;
  }

  function downloadFile(content, filename, mimeType) {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  const handleDownload = () => {
    if (format === 'csv') downloadCSV();
    else downloadXML();
  };

  // ── Styles ──
  const cardStyle = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px 16px', textAlign: 'center', minWidth: 120 };
  const btnStyle = (active) => ({ padding: '6px 14px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, fontWeight: active ? 600 : 400, background: active ? '#2563eb' : '#fff', color: active ? '#fff' : 'var(--theme-text)', cursor: 'pointer' });
  const thStyle = { padding: '6px 10px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontSize: 12, fontWeight: 600, color: '#6b7280', whiteSpace: 'nowrap' };
  const tdStyle = { padding: '5px 10px', borderBottom: '1px solid #f3f4f6', fontSize: 12, whiteSpace: 'nowrap' };
  const tdRight = { ...tdStyle, textAlign: 'right', fontFamily: 'monospace' };

  if (!selectedProperty?.id) return <div style={{ padding: 24 }}>Select a property first.</div>;

  const totalRows = (data?.meta?.pms_count || 0) + (data?.meta?.outlet_count || 0);
  const noConfig = config && !config.updated_at;

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 700, color: '#111827' }}>Export to Tally</h2>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: '#6b7280' }}>Export PMS, KOT, and BAR financial data for Tally Prime import.</p>

      {noConfig && (
        <div style={{ padding: 8, background: '#fef3c7', color: '#92400e', borderRadius: 4, marginBottom: 12, fontSize: 13 }}>
          Export Settings not configured. Default Tally ledger names will be used. Go to Export Settings to customize.
        </div>
      )}

      {/* Filter Bar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', marginBottom: 16 }}>
        <div>
          <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 2 }}>From</label>
          <input type="date" value={fromDate} onChange={e => { const v = e.target.value; setFromDate(v); if (toDate && v && toDate < v) setToDate(v); }} style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }} />
        </div>
        <div>
          <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 2 }}>To</label>
          <input type="date" value={toDate} min={fromDate || undefined} onChange={e => { const v = e.target.value; setToDate(v); if (fromDate && v && fromDate > v) setFromDate(v); }} style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }} />
        </div>
        <div>
          <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 2 }}>Module</label>
          <div style={{ display: 'flex', gap: 2 }}>
            {['ALL', 'PMS', 'KOT', 'BAR'].map(m => (
              <button key={m} onClick={() => setModule(m)} style={btnStyle(module === m)}>{m === 'ALL' ? 'All' : m}</button>
            ))}
          </div>
        </div>
        <div>
          <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 2 }}>Mode</label>
          <div style={{ display: 'flex', gap: 2 }}>
            <button onClick={() => setMode('detail')} style={btnStyle(mode === 'detail')}>Detail</button>
            <button onClick={() => setMode('summary')} style={btnStyle(mode === 'summary')}>Summary</button>
          </div>
        </div>
        <div>
          <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 2 }}>Format</label>
          <div style={{ display: 'flex', gap: 2 }}>
            <button onClick={() => setFormat('csv')} style={btnStyle(format === 'csv')}>CSV</button>
            <button onClick={() => setFormat('xml')} style={btnStyle(format === 'xml')}>Tally XML</button>
          </div>
        </div>
        <button onClick={handlePreview} disabled={loading} style={{ padding: '8px 20px', background: loading ? '#9ca3af' : '#2563eb', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 13, cursor: loading ? 'not-allowed' : 'pointer', height: 35 }}>
          {loading ? 'Loading...' : 'Preview'}
        </button>
        {data && totalRows > 0 && (
          <button onClick={handleDownload} style={{ padding: '8px 20px', background: '#059669', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 13, cursor: 'pointer', height: 35 }}>
            Download {format === 'csv' ? 'CSV' : 'XML'}
          </button>
        )}
      </div>

      {error && <div style={{ padding: 8, background: '#fee2e2', color: '#991b1b', borderRadius: 4, marginBottom: 12, fontSize: 13 }}>{error}</div>}

      {/* Summary Cards */}
      {data && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          <div style={cardStyle}><div style={{ fontSize: 11, color: '#6b7280' }}>Total Charges</div><div style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>{fmtMoney(totals.charges)}</div></div>
          <div style={cardStyle}><div style={{ fontSize: 11, color: '#6b7280' }}>Total Payments</div><div style={{ fontSize: 18, fontWeight: 700, color: '#059669' }}>{fmtMoney(totals.payments)}</div></div>
          <div style={cardStyle}><div style={{ fontSize: 11, color: '#6b7280' }}>Total Tax</div><div style={{ fontSize: 18, fontWeight: 700, color: 'var(--theme-warning)' }}>{fmtMoney(totals.tax)}</div></div>
          <div style={cardStyle}><div style={{ fontSize: 11, color: '#6b7280' }}>Total Rows</div><div style={{ fontSize: 18, fontWeight: 700, color: 'var(--theme-text)' }}>{totalRows}</div></div>
        </div>
      )}

      {/* Preview Table */}
      {data && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'auto', maxHeight: 500 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 1 }}>
              {mode === 'detail' ? (
                <tr>
                  <th style={thStyle}>Date</th><th style={thStyle}>Source</th><th style={thStyle}>Invoice</th>
                  <th style={thStyle}>Type</th><th style={thStyle}>Description</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Debit</th><th style={{ ...thStyle, textAlign: 'right' }}>Credit</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>CGST</th><th style={{ ...thStyle, textAlign: 'right' }}>SGST</th><th style={{ ...thStyle, textAlign: 'right' }}>IGST</th>
                  <th style={thStyle}>Guest</th><th style={thStyle}>Room</th>
                </tr>
              ) : (
                <tr>
                  <th style={thStyle}>Date</th><th style={thStyle}>Source</th><th style={thStyle}>Category</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Charges</th><th style={{ ...thStyle, textAlign: 'right' }}>Tax</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>CGST</th><th style={{ ...thStyle, textAlign: 'right' }}>SGST</th><th style={{ ...thStyle, textAlign: 'right' }}>IGST</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Payments</th><th style={{ ...thStyle, textAlign: 'right' }}>Count</th>
                </tr>
              )}
            </thead>
            <tbody>
              {mode === 'detail' ? (
                <>
                  {(data.pms_data || []).map((r, i) => (
                    <tr key={`p${i}`} style={{ background: i % 2 ? '#f9fafb' : '#fff' }}>
                      <td style={tdStyle}>{r.posting_date}</td>
                      <td style={tdStyle}><span style={{ background: '#dbeafe', color: '#1e40af', padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600 }}>PMS</span></td>
                      <td style={tdStyle}>{r.invoice_no || '-'}</td>
                      <td style={tdStyle}>{POSTING_TYPE_LABELS[r.posting_type] || r.posting_type}</td>
                      <td style={tdStyle}>{r.revenue_desc || r.remarks || ''}</td>
                      <td style={tdRight}>{fmtMoney(r.debit_amount)}</td>
                      <td style={tdRight}>{fmtMoney(r.credit_amount)}</td>
                      <td style={tdRight}>{fmtMoney(r.cgst_amount)}</td>
                      <td style={tdRight}>{fmtMoney(r.sgst_amount)}</td>
                      <td style={tdRight}>{fmtMoney(r.igst_amount)}</td>
                      <td style={tdStyle}>{r.guest_name || ''}</td>
                      <td style={tdStyle}>{r.room_number || ''}</td>
                    </tr>
                  ))}
                  {(data.outlet_data || []).map((r, i) => (
                    <tr key={`o${i}`} style={{ background: (data.pms_data.length + i) % 2 ? '#f9fafb' : '#fff' }}>
                      <td style={tdStyle}>{r.posting_date}</td>
                      <td style={tdStyle}><span style={{ background: r.outlet_type === 'BAR' ? '#fde68a' : '#d1fae5', color: r.outlet_type === 'BAR' ? '#92400e' : '#065f46', padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600 }}>{r.outlet_type}</span></td>
                      <td style={tdStyle}>-</td>
                      <td style={tdStyle}>Order Settlement</td>
                      <td style={tdStyle}>{r.outlet_name || r.outlet_type} #{r.order_no}</td>
                      <td style={tdRight}>{fmtMoney(r.subtotal)}</td>
                      <td style={tdRight}>{fmtMoney(r.total_amount)}</td>
                      <td style={tdRight}>{fmtMoney(r.cgst_amount)}</td>
                      <td style={tdRight}>{fmtMoney(r.sgst_amount)}</td>
                      <td style={tdRight}>{fmtMoney(r.igst_amount)}</td>
                      <td style={tdStyle}>{r.guest_name || ''}</td>
                      <td style={tdStyle}>{r.room_number || ''}</td>
                    </tr>
                  ))}
                </>
              ) : (
                <>
                  {(data.pms_data || []).map((r, i) => (
                    <tr key={`p${i}`} style={{ background: i % 2 ? '#f9fafb' : '#fff' }}>
                      <td style={tdStyle}>{r.posting_date}</td>
                      <td style={tdStyle}><span style={{ background: '#dbeafe', color: '#1e40af', padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600 }}>PMS</span></td>
                      <td style={tdStyle}>{r.revenue_desc || r.posting_type}</td>
                      <td style={tdRight}>{fmtMoney(r.total_debit)}</td>
                      <td style={tdRight}>{fmtMoney(r.total_tax)}</td>
                      <td style={tdRight}>{fmtMoney(r.total_cgst)}</td>
                      <td style={tdRight}>{fmtMoney(r.total_sgst)}</td>
                      <td style={tdRight}>{fmtMoney(r.total_igst)}</td>
                      <td style={tdRight}>{fmtMoney(r.total_credit)}</td>
                      <td style={tdRight}>{r.txn_count}</td>
                    </tr>
                  ))}
                  {(data.outlet_data || []).map((r, i) => (
                    <tr key={`o${i}`} style={{ background: (data.pms_data.length + i) % 2 ? '#f9fafb' : '#fff' }}>
                      <td style={tdStyle}>{r.posting_date}</td>
                      <td style={tdStyle}><span style={{ background: r.outlet_type === 'BAR' ? '#fde68a' : '#d1fae5', color: r.outlet_type === 'BAR' ? '#92400e' : '#065f46', padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600 }}>{r.outlet_type}</span></td>
                      <td style={tdStyle}>{r.outlet_type} - {r.payment_type}</td>
                      <td style={tdRight}>{fmtMoney(r.total_subtotal)}</td>
                      <td style={tdRight}>{fmtMoney(Number(r.total_cgst || 0) + Number(r.total_sgst || 0) + Number(r.total_igst || 0))}</td>
                      <td style={tdRight}>{fmtMoney(r.total_cgst)}</td>
                      <td style={tdRight}>{fmtMoney(r.total_sgst)}</td>
                      <td style={tdRight}>{fmtMoney(r.total_igst)}</td>
                      <td style={tdRight}>{fmtMoney(r.total_amount)}</td>
                      <td style={tdRight}>{r.txn_count}</td>
                    </tr>
                  ))}
                </>
              )}
              {totalRows === 0 && (
                <tr><td colSpan={12} style={{ ...tdStyle, textAlign: 'center', padding: 24, color: '#9ca3af' }}>No data found for the selected filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
